import { useCallback, useEffect, useMemo, useState } from 'react'
import { DRAW_BET_TYPES, DRAW_BET_TYPE_DIGITS } from '@nexbit/types'
import { ChoiceSelect } from '@/components/choice-select'
import { betChoices } from '@/lib/bet-choices'
import type { DrawBetType, DrawLotteryWithCurrent, DrawNumberCap, DrawNumberCapInput } from '@nexbit/types'
import {
  apiDrawSetNumberCaps, apiDrawDeleteNumberCap, apiDrawExposure, ApiRequestError, isOtpRequiredError,
} from '@/lib/api'
import { OtpConfirmDialog } from '@/components/OtpConfirmDialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, ArrowDown, ArrowUp, ChevronsUpDown, Pencil, Plus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'
import type { Dict } from '@/lib/i18n'
import {
  DEFAULT_SORT, lossIfHit, nextSort, sortCaps, type SortKey, type SortState,
} from './number-caps-sort'

/**
 * เพดานรายเลข — the house's ceiling on individual numbers.
 *
 * A bet type has one default ceiling per number. This is where one number is given a
 * different one — "เลข 123 รับ 20,000" while its neighbours stay at 5,000, or a hot number
 * throttled to 1,000 instead of being closed outright. A number with no row here simply
 * keeps the default, so the panel only ever lists the exceptions.
 *
 * One table per bet type, because that is how the exceptions are managed: 3 ตัวบน is one
 * book and 2 ตัวล่าง is another, and a single list filtered by a chip hides the rest.
 */

const REFRESH_MS = 5000
const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const CHOICE_WORDS = ['high', 'low', 'odd', 'even']

interface Props {
  lottery: DrawLotteryWithCurrent
  /** The lottery's ceilings, loaded by the page — it needs the same list for its stake table. */
  caps: DrawNumberCap[]
  state: 'loading' | 'ready' | 'error'
  /** Called after a write, so the page re-reads the list this panel is showing. */
  onChanged: () => Promise<void>
  /** The type the add box writes to — shared with the rest of the page's chips. */
  betType: DrawBetType
  onSelectBetType: (bt: DrawBetType) => void
  /** The open งวด, if any: the stake already taken is read from it. */
  liveDrawId: string | null
  canEdit: boolean
}

/** Split a typed batch on spaces and commas, drop blanks and repeats. */
function parseNumbers(raw: string): string[] {
  return [...new Set(raw.split(/[\s,]+/).map((s) => s.trim().toLowerCase()).filter(Boolean))]
}

/** Which of the typed numbers are the wrong shape for the bet type. */
function badNumbers(betType: DrawBetType, numbers: string[]): string[] {
  const digits = DRAW_BET_TYPE_DIGITS[betType]
  return numbers.filter((n) => (digits > 0 ? !new RegExp(`^\\d{${digits}}$`).test(n) : !CHOICE_WORDS.includes(n)))
}

export default function NumberCapsPanel({
  lottery, caps, state, onChanged, betType, onSelectBetType, liveDrawId, canEdit,
}: Props) {
  const t = useT()

  const [taken, setTaken] = useState<Map<string, number>>(new Map())
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [addNumbers, setAddNumbers] = useState('')
  const [addCap, setAddCap] = useState('')

  // One order for every bet type's table: an admin hunting the heaviest number wants the
  // same column pressed everywhere, not once per book.
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)

  // A change that RAISES a ceiling waits here for its code. Lowering one never does: it only
  // restricts what the house takes, and it is the lever for a number running hot mid-draw.
  type Pending =
    | { kind: 'set'; caps: DrawNumberCapInput[] }
    | { kind: 'remove'; cap: DrawNumberCap; def: number }
  const [pending, setPending] = useState<Pending | null>(null)

  const cfg = lottery.betConfig[betType]
  const digits = DRAW_BET_TYPE_DIGITS[betType]
  const choices = betChoices(betType)

  // Switching bet type must not carry text that means nothing in the new one: '45' typed
  // for 2top is not a choice, and a select holding it shows blank while the submit still
  // reads it as a bad number — a field that looks empty and refuses to save.
  //
  // Only text that is INVALID for the new type is dropped, never all of it. The pencil on a
  // row selects that row's type and fills its number in one go, and clearing unconditionally
  // here would wipe the number it had just put there.
  useEffect(() => {
    setAddNumbers((cur) => (badNumbers(betType, parseNumbers(cur)).length > 0 ? '' : cur))
  }, [betType])

  // The types that have an exception, as a string so the effect below depends on a value
  // and not on a list the page rebuilds every poll.
  const typesKey = [...new Set(caps.map((c) => c.betType))].sort().join(',')

  // The stake already taken on each excepted number — one call per type, because the
  // endpoint returns only the top 100 numbers across types otherwise.
  const loadTaken = useCallback(async () => {
    const types = typesKey === '' ? [] : (typesKey.split(',') as DrawBetType[])
    if (!liveDrawId || types.length === 0) { setTaken(new Map()); return }
    try {
      const exps = await Promise.all(types.map((bt) => apiDrawExposure(liveDrawId, bt)))
      const m = new Map<string, number>()
      for (const e of exps) for (const n of e.byNumber) m.set(`${n.betType}|${n.number}`, Number(n.stake))
      setTaken(m)
    } catch { /* the stake is a nicety; the ceilings themselves are already on screen */ }
  }, [liveDrawId, typesKey])

  useEffect(() => {
    void loadTaken()
    const id = setInterval(() => void loadTaken(), REFRESH_MS)
    return () => clearInterval(id)
  }, [loadTaken])

  const sections = useMemo(
    () => DRAW_BET_TYPES
      .map((bt) => ({ betType: bt, rows: caps.filter((c) => c.betType === bt) }))
      .filter((s) => s.rows.length > 0),
    [caps],
  )

  const numbers = parseNumbers(addNumbers)
  const bad = badNumbers(betType, numbers)
  const amount = addCap.trim() === '' ? null : Number(addCap)
  const belowMin = amount !== null && cfg !== undefined && amount < cfg.minBet
  const canSubmit = canEdit && !saving && numbers.length > 0 && bad.length === 0
    && amount !== null && amount > 0 && !belowMin

  /** What is in force on a number today: its own ceiling if it has one, else the type's default. */
  const currentCap = useCallback((bt: DrawBetType, number: string): number => {
    const own = caps.find((c) => c.betType === bt && c.number === number)
    return own ? Number(own.maxPerNumber) : (lottery.betConfig[bt]?.maxPerNumber ?? 0)
  }, [caps, lottery])

  async function submit() {
    if (!canSubmit || amount === null) return
    setActionError(null)
    setNotice(null)
    const batch: DrawNumberCapInput[] = numbers.map((number) => ({ betType, number, maxPerNumber: amount }))

    // Worked out here so the code is asked for up front. The server decides again against
    // what it holds, and if the two disagree the refusal below opens the same dialog.
    if (batch.some((c) => c.maxPerNumber > currentCap(c.betType, c.number))) {
      setPending({ kind: 'set', caps: batch })
      return
    }
    setSaving(true)
    try {
      await apiDrawSetNumberCaps(lottery.id, batch)
      await afterSet(batch.length)
    } catch (e) {
      if (isOtpRequiredError(e)) setPending({ kind: 'set', caps: batch })
      else setActionError(e instanceof ApiRequestError ? e.message : t.common.error)
    } finally {
      setSaving(false)
    }
  }

  async function afterSet(count: number) {
    setNotice(t.numberCaps.saved(count))
    setAddNumbers('')
    setAddCap('')
    await onChanged()
  }

  async function remove(c: DrawNumberCap) {
    setActionError(null)
    setNotice(null)
    // Removing an override puts the number back on the default — a RAISE if it was lower.
    const def = lottery.betConfig[c.betType]?.maxPerNumber ?? 0
    if (Number(c.maxPerNumber) < def) { setPending({ kind: 'remove', cap: c, def }); return }
    try {
      await apiDrawDeleteNumberCap(c.id)
      await onChanged()
    } catch (e) {
      if (isOtpRequiredError(e)) setPending({ kind: 'remove', cap: c, def })
      else setActionError(e instanceof ApiRequestError ? e.message : t.common.error)
    }
  }

  function edit(c: DrawNumberCap) {
    onSelectBetType(c.betType)
    setAddNumbers(c.number)
    setAddCap(String(Math.round(Number(c.maxPerNumber))))
  }

  const inputError = bad.length > 0
    ? (digits > 0 ? t.numberCaps.badDigits(digits, bad.join(' ')) : t.numberCaps.badChoice(bad.join(' ')))
    : belowMin && cfg ? t.numberCaps.belowMin(nf.format(cfg.minBet)) : null

  return (
    <div className="mt-4 rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
      <div className="border-b border-border bg-secondary px-4 py-2.5">
        <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
          {t.numberCaps.title}
        </span>
      </div>

      <div className="space-y-4 p-4">
        <p className="font-mono text-[11px] text-muted-foreground">{t.numberCaps.desc}</p>

        {/* Add box — writes to the selected bet type, several numbers at once. */}
        {canEdit && cfg && (
          <div className="rounded-[var(--radius-sm)] border border-border p-3">
            <div className="mb-2 font-mono text-[11px] text-muted-foreground">
              {t.draws.betTypeLabel[betType]} · {t.numberCaps.typeDefault(nf.format(cfg.maxPerNumber))}
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex min-w-[180px] flex-1 flex-col gap-1">
                <Label htmlFor="cap-numbers" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {digits > 0 ? t.numberCaps.addNumbers(digits) : t.numberCaps.addChoice}
                </Label>
                {choices ? (
                  // สูง/ต่ำ and คู่/คี่ have two answers each: pick them, do not spell them.
                  <ChoiceSelect
                    id="cap-numbers" choices={choices} allowBoth
                    value={addNumbers} onChange={setAddNumbers}
                    invalid={bad.length > 0}
                  />
                ) : (
                  <Input
                    id="cap-numbers" value={addNumbers}
                    onChange={(e) => setAddNumbers(e.target.value)}
                    placeholder={digits === 3 ? '123 456 789' : digits === 2 ? '45 67 89' : '1 2 3'}
                    aria-invalid={bad.length > 0}
                    className="h-9 font-mono text-sm"
                  />
                )}
              </div>
              <div className="flex w-36 flex-col gap-1">
                <Label htmlFor="cap-amount" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {t.numberCaps.cap}
                </Label>
                <Input
                  id="cap-amount" inputMode="numeric" value={addCap}
                  onChange={(e) => setAddCap(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder={t.numberCaps.capPlaceholderHint(nf.format(cfg.maxPerNumber))}
                  aria-invalid={belowMin}
                  className="h-9 font-mono text-sm tabular-nums"
                />
              </div>
              <Button
                size="sm" disabled={!canSubmit} onClick={() => void submit()}
                className="h-9 gap-1.5 font-mono text-xs"
              >
                <Plus className="h-3 w-3" /> {t.numberCaps.set}
              </Button>
            </div>
            {/* What the number would cost if it came up — the figure the ceiling is weighed against. */}
            {amount !== null && amount > 0 && !belowMin && (
              <p className="mt-2 font-mono text-[11px] text-warning">
                {t.numberCaps.worstCase(nf.format(amount * cfg.payout))}
              </p>
            )}
            {inputError && <p className="mt-2 font-mono text-[11px] text-destructive">{inputError}</p>}
          </div>
        )}

        {notice && <p role="status" className="font-mono text-[11px] text-success">{notice}</p>}
        {actionError && <p role="alert" className="font-mono text-[11px] text-destructive">{actionError}</p>}

        {state === 'loading' ? (
          <div className="space-y-2"><Skeleton className="h-9 w-full" /><Skeleton className="h-24 w-full" /></div>
        ) : state === 'error' ? (
          <div className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-destructive/40 p-2">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
            <p className="font-mono text-xs text-destructive">{t.numberCaps.loadError}</p>
          </div>
        ) : sections.length === 0 ? (
          <p className="py-4 text-center font-mono text-[11px] text-muted-foreground">{t.numberCaps.empty}</p>
        ) : (
          <div className="space-y-4">
            {sections.map((s) => (
              <TypeSection
                key={s.betType} t={t} betType={s.betType} rows={s.rows}
                cfg={lottery.betConfig[s.betType]} taken={taken} canEdit={canEdit}
                selected={s.betType === betType} hasLive={liveDrawId !== null}
                sort={sort} onSort={(key) => setSort((cur) => nextSort(cur, key))}
                onSelect={() => onSelectBetType(s.betType)} onEdit={edit} onRemove={(c) => void remove(c)}
              />
            ))}
          </div>
        )}
      </div>

      {pending && (
        <OtpConfirmDialog
          purpose="draw.number-cap.loosen"
          // Exactly what the request will carry, so the code the server issues for it verifies.
          payload={pending.kind === 'set'
            ? { params: { lotteryId: lottery.id }, body: { caps: pending.caps } }
            : { params: { id: pending.cap.id }, body: {} }}
          title={t.numberCaps.otpTitle}
          description={pending.kind === 'set'
            ? t.numberCaps.otpSetDesc(
                pending.caps.length,
                pending.caps.slice(0, 3)
                  .map((c) => `${c.number} → ${nf.format(c.maxPerNumber)}`).join(', ')
                  + (pending.caps.length > 3 ? ', …' : ''),
              )
            : t.numberCaps.otpRemoveDesc(
                pending.cap.number, nf.format(Number(pending.cap.maxPerNumber)), nf.format(pending.def),
              )}
          confirmLabel={t.numberCaps.otpConfirm}
          onConfirm={async (otp) => {
            if (pending.kind === 'set') await apiDrawSetNumberCaps(lottery.id, pending.caps, otp)
            else await apiDrawDeleteNumberCap(pending.cap.id, otp)
          }}
          onDone={() => {
            const done = pending
            setPending(null)
            if (done.kind === 'set') void afterSet(done.caps.length)
            else void onChanged()
          }}
          onClose={() => setPending(null)}
        />
      )}
    </div>
  )
}

/** One bet type's exceptions, with how each compares to the type's default. */
function TypeSection({
  t, betType, rows, cfg, taken, canEdit, selected, hasLive, sort, onSort, onSelect, onEdit, onRemove,
}: {
  t: Dict
  betType: DrawBetType
  rows: DrawNumberCap[]
  cfg: DrawLotteryWithCurrent['betConfig'][DrawBetType]
  taken: Map<string, number>
  canEdit: boolean
  selected: boolean
  hasLive: boolean
  sort: SortState
  onSort: (key: SortKey) => void
  onSelect: () => void
  onEdit: (c: DrawNumberCap) => void
  onRemove: (c: DrawNumberCap) => void
}) {
  const def = cfg?.maxPerNumber ?? 0
  const payout = cfg?.payout ?? 0
  const stakeOf = (c: DrawNumberCap) => taken.get(`${c.betType}|${c.number}`) ?? 0
  const ordered = useMemo(
    () => sortCaps(rows, sort, stakeOf, payout),
    // stakeOf is rebuilt every render; what it reads is `taken`, which is what matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, sort, taken, payout],
  )
  // The heaviest number in this book is the one an admin is looking for.
  const worstLoss = ordered.reduce((m, c) => Math.max(m, lossIfHit(stakeOf(c), payout)), 0)
  return (
    <section
      aria-label={t.draws.betTypeLabel[betType]}
      className={cn('rounded-[var(--radius-sm)] border', selected ? 'border-primary/60' : 'border-border')}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-secondary/60 px-3 py-2">
        <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
          {t.draws.betTypeLabel[betType]}
        </span>
        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">({rows.length})</span>
        <span className="rounded-[var(--radius-sm)] border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          {t.numberCaps.typeDefault(nf.format(def))}
        </span>
        {canEdit && (selected ? (
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-primary">{t.numberCaps.editing}</span>
        ) : (
          <Button size="sm" variant="ghost" onClick={onSelect} className="ml-auto h-7 gap-1 font-mono text-[11px]">
            <Pencil className="h-3 w-3" /> {t.numberCaps.addToType}
          </Button>
        ))}
      </div>
      {/* Fixed layout: every bet type is its own table, and auto widths put the columns of
          one type a few pixels off those of the next. */}
      <Table className="min-w-[780px] table-fixed">
        <TableHeader>
          <TableRow>
            <SortHead t={t} sortKey="number" sort={sort} onSort={onSort} label={t.numberCaps.colNumber} className="w-28" />
            <SortHead t={t} sortKey="cap" sort={sort} onSort={onSort} label={t.numberCaps.colCap} className="w-40" align="right" />
            <TableHead className="font-mono text-[10px] uppercase tracking-widest">{t.numberCaps.colVsDefault}</TableHead>
            <SortHead t={t} sortKey="loss" sort={sort} onSort={onSort} label={t.numberCaps.colLoss} className="w-44" align="right" title={t.numberCaps.lossHint} />
            <TableHead className="w-52 text-right font-mono text-[10px] uppercase tracking-widest">{t.numberCaps.colTaken}</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {ordered.map((c) => {
            const cap = Number(c.maxPerNumber)
            const diff = cap - def
            const got = stakeOf(c)
            const loss = lossIfHit(got, payout)
            const pct = cap > 0 ? Math.min(100, Math.round((got / cap) * 100)) : 0
            return (
              <TableRow key={c.id}>
                <TableCell className="font-mono text-sm font-semibold tabular-nums tracking-widest">{c.number}</TableCell>
                <TableCell className="text-right font-mono text-xs tabular-nums text-foreground">{nf.format(cap)}</TableCell>
                <TableCell className="font-mono text-[11px]">
                  {/* Raising a ceiling is the direction that adds risk, so it is the one that is coloured. */}
                  {diff > 0 ? <span className="text-warning">{t.numberCaps.higher(nf.format(diff))}</span>
                    : diff < 0 ? <span className="text-muted-foreground">{t.numberCaps.lower(nf.format(-diff))}</span>
                    : <span className="text-muted-foreground">{t.numberCaps.same}</span>}
                  {cfg && cap > cfg.maxPerType && (
                    <div className="text-[10px] text-destructive">{t.numberCaps.overPool(nf.format(cfg.maxPerType))}</div>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs tabular-nums">
                  {/* Two figures, because they answer two questions: what is at stake on this
                      number NOW, and what the ceiling the admin set could cost at its worst. */}
                  {hasLive ? (
                    <span className={cn(loss > 0 && loss === worstLoss ? 'font-semibold text-destructive' : 'text-warning')}>
                      {nf.format(loss)}
                    </span>
                  ) : <span className="text-muted-foreground">—</span>}
                  <div className="text-[10px] text-muted-foreground">{t.numberCaps.lossIfFull(nf.format(lossIfHit(cap, payout)))}</div>
                </TableCell>
                <TableCell className="text-right font-mono text-xs tabular-nums">
                  {hasLive ? (
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn('h-full rounded-full', pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-primary')}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className={cn('shrink-0', pct >= 100 ? 'font-semibold text-destructive' : 'text-muted-foreground')}>
                        {nf.format(got)}
                      </span>
                    </div>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  {canEdit && (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => onEdit(c)} className="h-7 w-7 p-0" aria-label={`${t.numberCaps.edit} ${c.number}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm" variant="ghost" onClick={() => onRemove(c)}
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        aria-label={`${t.numberCaps.remove} ${c.number}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </section>
  )
}

/**
 * A column header that sorts. The state is on the <th> (aria-sort) and not only in the
 * arrow, so a screen reader hears which column the table is ordered by and which way.
 */
function SortHead({
  t, sortKey, sort, onSort, label, className, align = 'left', title,
}: {
  t: Dict
  sortKey: SortKey
  sort: SortState
  onSort: (key: SortKey) => void
  label: string
  className?: string
  align?: 'left' | 'right'
  title?: string
}) {
  const active = sort.key === sortKey
  const Icon = !active ? ChevronsUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <TableHead
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn('p-0', className)}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={title ?? t.numberCaps.sortHint(label)}
        className={cn(
          'flex h-10 w-full items-center gap-1 px-2 font-mono text-[10px] uppercase tracking-widest transition-colors',
          'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
          align === 'right' ? 'justify-end' : 'justify-start',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
        <Icon className={cn('h-3 w-3 shrink-0', !active && 'opacity-50')} aria-hidden />
      </button>
    </TableHead>
  )
}
